import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Save, BadgePercent } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, erroApi } from './api';
import { formatarPreco } from '../data/produtos';

function statusPromo(p) {
  if (p.precoPromocional == null) return { rotulo: 'Sem promoção', classe: 'bg-slate-100 text-slate-500' };
  const agora = new Date();
  if (p.promoInicio && new Date(p.promoInicio) > agora) return { rotulo: 'Programada', classe: 'bg-blue-100 text-mk-blue' };
  if (p.promoFim && new Date(p.promoFim) < agora) return { rotulo: 'Encerrada', classe: 'bg-slate-200 text-slate-500' };
  return { rotulo: 'Ativa agora', classe: 'bg-red-100 text-mk-red' };
}

const paraInput = (iso) => (iso ? iso.slice(0, 16) : '');
const paraIso = (v) => (v ? `${v}:00-03:00` : null);

export default function Promocoes() {
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [edits, setEdits] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ['admin-promocoes', busca],
    queryFn: async () => (await apiFetch(`/admin/produtos?q=${encodeURIComponent(busca)}&limit=50`)).json(),
  });

  const valor = (p, campo, padrao) => (edits[p.id]?.[campo] ?? padrao);
  const setCampo = (p, campo, v) => setEdits({ ...edits, [p.id]: { ...edits[p.id], [campo]: v } });

  const salvar = async (p) => {
    const e = edits[p.id] || {};
    const promo = e.precoPromocional !== undefined ? e.precoPromocional : p.precoPromocional;
    const payload = {
      nome: p.nome, sku: p.sku || '', categoria: p.categoria, marca: p.marca || null,
      descricao: p.descricao || '', detalhes: p.detalhes || [],
      precoVenda: p.precoVenda,
      precoPromocional: promo === '' || promo == null ? null : parseFloat(promo),
      promoInicio: e.promoInicio !== undefined ? paraIso(e.promoInicio) : p.promoInicio || null,
      promoFim: e.promoFim !== undefined ? paraIso(e.promoFim) : p.promoFim || null,
      parcelas: p.parcelas || null,
      estoqueCerroAzul: p.estoqueCerroAzul ?? null,
      estoqueDoutorUlysses: p.estoqueDoutorUlysses ?? null,
      estoqueMinimo: p.estoqueMinimo ?? 2,
      mostrarEstoque: Boolean(p.mostrarEstoque),
      destaque: Boolean(p.destaque),
      status: p.status, fotos: p.fotos,
    };
    if (payload.precoPromocional != null && (isNaN(payload.precoPromocional) || payload.precoPromocional >= p.precoVenda)) {
      toast.error('O preço promocional deve ser menor que o preço normal.');
      return;
    }
    const r = await apiFetch(`/admin/produtos/${p.id}`, { method: 'PUT', body: JSON.stringify(payload) });
    if (r.ok) {
      toast.success(`Promoção de "${p.nome}" salva.`);
      setEdits({ ...edits, [p.id]: undefined });
      qc.invalidateQueries({ queryKey: ['admin-promocoes'] });
      qc.invalidateQueries({ queryKey: ['admin-produtos'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
  };

  const encerrar = async (p) => {
    setCampo(p, 'precoPromocional', '');
    const payload = {
      nome: p.nome, sku: p.sku || '', categoria: p.categoria, marca: p.marca || null,
      descricao: p.descricao || '', detalhes: p.detalhes || [],
      precoVenda: p.precoVenda, precoPromocional: null, promoInicio: null, promoFim: null,
      parcelas: p.parcelas || null,
      estoqueCerroAzul: p.estoqueCerroAzul ?? null,
      estoqueDoutorUlysses: p.estoqueDoutorUlysses ?? null,
      estoqueMinimo: p.estoqueMinimo ?? 2,
      mostrarEstoque: Boolean(p.mostrarEstoque),
      destaque: Boolean(p.destaque),
      status: p.status, fotos: p.fotos,
    };
    const r = await apiFetch(`/admin/produtos/${p.id}`, { method: 'PUT', body: JSON.stringify(payload) });
    if (r.ok) {
      toast.success(`Promoção de "${p.nome}" encerrada. Preço normal restaurado.`);
      qc.invalidateQueries({ queryKey: ['admin-promocoes'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    }
  };

  const itens = useMemo(() => data?.items || [], [data]);

  return (
    <div data-testid="admin-promocoes" className="max-w-4xl">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900">
        <BadgePercent size={24} className="text-mk-red" /> Produtos em promoção
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Defina o preço promocional e, se quiser, programe início e encerramento (horário de Brasília). Fora do período, o preço normal volta automaticamente.
      </p>

      <div className="relative mt-4 max-w-md">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="promo-search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto..." className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-mk-blue" />
      </div>

      {isLoading ? (
        <p className="mt-8 text-sm text-slate-400">Carregando...</p>
      ) : (
        <div className="mt-4 space-y-3">
          {itens.map((p) => {
            const st = statusPromo(p);
            const promoValor = valor(p, 'precoPromocional', p.precoPromocional ?? '');
            return (
              <div key={p.id} data-testid={`promo-row-${p.id}`} className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <img src={p.fotos?.[0]} alt="" className="h-11 w-11 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-slate-800">{p.nome}</p>
                    <p className="text-xs text-slate-400">Preço normal: {formatarPreco(p.precoVenda)}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-extrabold ${st.classe}`}>{st.rotulo}</span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-4">
                  <div>
                    <label className="text-[11px] font-bold uppercase text-slate-400">Preço promocional</label>
                    <input
                      data-testid={`promo-preco-${p.id}`}
                      type="number" min="0" step="0.01" placeholder="Sem promoção"
                      value={promoValor}
                      onChange={(e) => setCampo(p, 'precoPromocional', e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-mk-red"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase text-slate-400">Início (opcional)</label>
                    <input data-testid={`promo-inicio-${p.id}`} type="datetime-local" value={valor(p, 'promoInicio', paraInput(p.promoInicio))} onChange={(e) => setCampo(p, 'promoInicio', e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-mk-blue" />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase text-slate-400">Fim (opcional)</label>
                    <input data-testid={`promo-fim-${p.id}`} type="datetime-local" value={valor(p, 'promoFim', paraInput(p.promoFim))} onChange={(e) => setCampo(p, 'promoFim', e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-mk-blue" />
                  </div>
                  <div className="flex items-end gap-2">
                    <button data-testid={`promo-save-${p.id}`} onClick={() => salvar(p)} className="inline-flex items-center gap-1.5 rounded-full bg-mk-blue px-4 py-2.5 text-xs font-bold text-white hover:bg-mk-bluelight">
                      <Save size={14} /> Salvar
                    </button>
                    {p.precoPromocional != null && (
                      <button data-testid={`promo-end-${p.id}`} onClick={() => encerrar(p)} className="rounded-full border border-slate-300 px-3 py-2.5 text-xs font-bold text-slate-500 hover:bg-slate-50">
                        Encerrar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {itens.length === 0 && <p className="rounded-2xl bg-white py-10 text-center text-sm text-slate-400">Nenhum produto encontrado.</p>}
        </div>
      )}
    </div>
  );
}
